import { useFormikContext } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  ICreateProposalForm,
  ICreateProposalProps,
  INftsWithTradeInfo,
} from "../../../../common/interfaces/proposals.interface";
import {
  customStyles,
  EMPTY_STRING,
  MESSAGE_TYPE,
  SOL_DECIMAL_PLACES,
  SOL_SYMBOL,
} from "../../../../common/constants/common.constants";
import WizardField from "../../../WizardField/WizardField";
import { ReactComponent as solseaLogo } from "../../../../assets/solseaLogo.svg";
import { ReactComponent as unqLogo } from "../../../../assets/unqSvgLogo.svg";
import "./SellNftProposal.scss";
import NftSelectOption from "../../../ClubDetails/OverviewClub/Staking/StakingModal/DepositStakingModal/NftSelectOption/NftSelectOption";
import formConfig from "../../formConfig";
import ProposalToopltip from "../ProposalTooptip/ProposalTooltip";
import { IDetailNftData } from "../../../../common/interfaces/common.interface";
import {
  ProposalBuyOption,
  SellProposalOption,
} from "../../../../common/enums/proposals.enums";
import SellOption from "./SellOption/SellOption";
import { clubsStore } from "../../../../contexts/clubStore";
import MintSelect from "../TransferProposal/MintSelect/MintSelect";
import { getTradeInfoForAllVaultNfts } from "../../../../solana/methods/proposals";
import { PublicKey } from "@solana/web3.js";
import { NATIVE_MINT } from "@solana/spl-token";
import SkeletonItem from "../../../SkeletonItem/SkeletonItem";
import { createNotification } from "../../../../utilities/notification";

const SellNftProposal: FC<{
  form: ICreateProposalProps;
  nftToSell?: IDetailNftData;
}> = ({ form, nftToSell }) => {
  const { vault, treasury } = clubsStore();

  const [isTootlipVisible, toggleIsTooltipVisible] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState<PublicKey>();
  const [nftsWithTradeInfo, setNftsnftsWithTradeInfo] =
    useState<INftsWithTradeInfo[]>();
  //Note: we set null here because react-select remove value only if is set to null
  const [selectedNft, setSelectedNft] = useState<
    IDetailNftData | undefined | null
  >(nftToSell);

  const { errors, setFieldValue, values } =
    useFormikContext<ICreateProposalForm>();

  useEffect(() => {
    void getNftsWithTradeInfo();
  }, [treasury, vault]);

  useEffect(() => {
    if (nftToSell) {
      setFieldValue(form.formFields.nft.name, nftToSell);
    }
    void selectCurrency(nftToSell ?? values.nft);
  }, [nftToSell, treasury, values.nft, nftsWithTradeInfo]);

  //TODO@goran:comment in this if we implement magic eden sell
  // const getNftCollectionInfo = async () => {
  //   try {
  //     const nftMint = values.nft.mint.toString();
  //     const nftData = await getNftDataFromMagicEden(nftMint);
  //     const collectionData = await getCollectionDataFromMagicEden(
  //       nftData.nft.collection
  //     );
  //     setCollectionData(collectionData);
  //     setNftData(nftData);
  //     setFieldValue(formConfig.formFields.magicEdenErrors.name, false);
  //     setFieldValue("isMagicEdenNft", true);
  //   } catch (error: any) {
  //     setFieldValue("isMagicEdenNft", false);
  //     setFieldValue(formConfig.formFields.magicEdenErrors.name, true);
  //     createNotification(
  //       MESSAGE_TYPE.ERROR,
  //       "Nft collection is not verified on Magic Eden"
  //     );
  //   }
  // };

  const getNftsWithTradeInfo = async () => {
    try {
      if (treasury && vault?.nfts) {
        setNftsnftsWithTradeInfo(
          await getTradeInfoForAllVaultNfts(treasury, vault?.nfts)
        );
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem with loading nft data");
    }
  };

  const selectCurrency = (nft: IDetailNftData | undefined) => {
    try {
      if (nftsWithTradeInfo && treasury && nft) {
        const ledgerEntryInfo = nftsWithTradeInfo.find(
          (item) => item.nftMint.toString() === nft.mint.toString()
        );
        setSelectedCurrency(
          ledgerEntryInfo?.tradeInfo?.currencyMint ??
            treasury.denominatedTokenInfo?.mint ??
            NATIVE_MINT
        );
        setFieldValue(
          "mint",
          ledgerEntryInfo?.tradeInfo?.currencyMint ??
            treasury.denominatedTokenInfo?.mint ??
            NATIVE_MINT
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isNFtOptionDisabled = useMemo(() => {
    return (option: IDetailNftData) => {
      return (
        !!nftsWithTradeInfo &&
        values.sellOption === ProposalBuyOption.Solsea &&
        nftsWithTradeInfo
          .find((item) => item.nftMint.toString() === option.mint.toString())
          ?.tradeInfo?.currencyMint?.toString() !== NATIVE_MINT.toString()
      );
    };
  }, [nftsWithTradeInfo, values.sellOption]);

  return (
    <div className="sell-nft-proposal">
      <div className="sell-nft-proposal__sell-option">
        {isTootlipVisible && (
          <div className="sell-nft-proposal__tooltip">
            <ProposalToopltip
              handleMouseEnter={() => toggleIsTooltipVisible(true)}
              handleMouseLeave={() => toggleIsTooltipVisible(false)}
              tooltipData={[
                {
                  tooltipDescription:
                    "Sell NFTs on the Magic Eden marketplace.Note that due to Magic Eden's API architecture an intermediary custody wallet is used for every transaction.",
                },
              ]}
            />
          </div>
        )}
        <div className="sell-nft-proposal__title-tooltip">
          <p>I want to sell NFT through</p>
        </div>
        <div className="sell-nft-proposal__options">
          <SellOption
            isActive={values.sellOption === SellProposalOption.PeerToPeer}
            title="Peer to Peer"
            handleClick={() => {
              setFieldValue(
                formConfig.formFields.sellOption.name,
                SellProposalOption.PeerToPeer
              );
              setFieldValue(formConfig.formFields.nft.name, EMPTY_STRING);
              setSelectedNft(null);
            }}
            imageUrl={{ logo: unqLogo }}
          />
          <SellOption
            isActive={values.sellOption === SellProposalOption.Solsea}
            title="SolSea"
            handleClick={() => {
              setFieldValue(
                formConfig.formFields.sellOption.name,
                SellProposalOption.Solsea
              );
              setFieldValue(formConfig.formFields.nft.name, EMPTY_STRING);
              setSelectedNft(null);
            }}
            imageUrl={{ logo: solseaLogo }}
          />
        </div>
      </div>
      {nftsWithTradeInfo ? (
        <div className="sell-nft-proposal__select-price">
          <div className="create-proposal__choose-nft-wrapper">
            <p className="create-proposal__choose-nft-title">
              Choose NFT from Vault
            </p>
            <Select
              name={form.formFields.nft.name}
              styles={customStyles}
              options={vault!.nfts}
              value={selectedNft}
              isClearable={true}
              isOptionDisabled={(option) => {
                return isNFtOptionDisabled(option);
              }}
              onChange={(e) => {
                setFieldValue(form.formFields.nft.name, e);
                setSelectedNft(e);
              }}
              defaultValue={null}
              formatOptionLabel={(nft: IDetailNftData) => {
                return (
                  <NftSelectOption
                    nft={nft}
                    isOptionDisabled={isNFtOptionDisabled(nft)}
                    tradeInfo={
                      nftsWithTradeInfo.find(
                        (item) =>
                          item.nftMint.toString() === nft.mint.toString()
                      )?.tradeInfo
                    }
                  />
                );
              }}
            />
            {errors.nft && <p className="nft-option__error">{errors.nft}</p>}
          </div>
          <div className="sell-nft-proposal__price-container">
            <WizardField
              type="number"
              name={form.formFields.price.name}
              label={form.formFields.price.label}
              placeholder="NFT price here"
              maxDecimalsLength={SOL_DECIMAL_PLACES}
              required
              persistentPlaceholder={
                values.sellOption !== ProposalBuyOption.PeerToPeer
                  ? SOL_SYMBOL
                  : undefined
              }
            />
            {treasury &&
              values.sellOption === ProposalBuyOption.PeerToPeer &&
              selectedCurrency && (
                <MintSelect
                  treasuryTokenAccounts={treasury.tokenAccounts}
                  isForm
                  setFieldValue={setFieldValue}
                  chosenMint={selectedCurrency.toString()}
                  isDisabled
                />
              )}
          </div>
        </div>
      ) : (
        <SkeletonItem height={50} />
      )}
      {values.sellOption !== SellProposalOption.Solsea && (
        <WizardField
          type="text"
          placeholder="Buyer address"
          name={form.formFields.buyerAddress.name}
          label={form.formFields.buyerAddress.label}
        />
      )}
    </div>
  );
};

export default SellNftProposal;
